import { useEffect } from "react";

/**
 * Read a `?value=` (or alias) URL search parameter once on mount and hand it
 * to the caller. Used by tools that want to support deep-links from the
 * guides — a `<TryTool>` widget points at e.g. `/gtin-validator?value=5012345678900`,
 * and the validator picks the value up here and applies it as initial state.
 *
 * Implemented as an effect rather than `useState(() => readQuery())` so it is
 * SSR-safe: the server renders with the tool's hard-coded default, the client
 * then applies the URL value on mount. That avoids hydration mismatches and
 * keeps each tool's existing default as the empty-URL behaviour.
 */
export function usePrefillValue(
  apply: (value: string) => void,
  options?: { paramNames?: string[]; onApplied?: () => void },
): void {
  useEffect(() => {
    if (typeof window === "undefined") return;
    const params = new URLSearchParams(window.location.search);
    const names = options?.paramNames ?? ["value", "v"];
    let found: string | null = null;
    for (const n of names) {
      const got = params.get(n);
      if (got !== null && got !== "") {
        found = got;
        break;
      }
    }
    if (found !== null) {
      apply(found);
      options?.onApplied?.();
    }
    // Empty deps — fire once on mount; subsequent in-tool edits are owned by
    // the tool's own state.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
}
